import { useMemo } from 'react'
import { useResolvePublicProfilesQuery } from 'state/services/community/community'
import { avatarUrl } from 'utils/avatarUrl'

const shorten = (address: string) => (address ? `${address.slice(0, 6)}…${address.slice(-4)}` : '')

/**
 * How a person should be shown: their public name if they chose one, and their
 * address otherwise.
 *
 * The fallback is the point. A profile is private by default, so most wallets
 * will never resolve to a name — this must therefore be a display convenience
 * that degrades to exactly what the screen showed before, never a lookup that
 * blocks a render or leaves a gap where a person's identity should be.
 */
export function useDisplayNames(wallets: (string | undefined | null)[]): {
  displayName: (wallet: string | undefined | null) => string
  isNamed: (wallet: string | undefined | null) => boolean
  /** The picture, where its owner is public and set one. */
  avatarFor: (wallet: string | undefined | null) => string | undefined
} {
  const list = useMemo(
    () =>
      Array.from(
        new Set(
          wallets.filter((w): w is string => typeof w === 'string' && w.length === 42).map((w) => w.toLowerCase())
        )
      ),
    [wallets]
  )

  const { data } = useResolvePublicProfilesQuery({ wallets: list }, { skip: list.length === 0 })

  return useMemo(() => {
    const profiles = data?.profiles ?? {}
    const nameFor = (wallet: string | undefined | null) => {
      if (!wallet) return undefined
      const p = profiles[wallet.toLowerCase()]
      return p?.name || (p?.tag ? `@${p.tag}` : undefined)
    }
    return {
      displayName: (wallet) => nameFor(wallet) ?? shorten(wallet ?? ''),
      isNamed: (wallet) => Boolean(nameFor(wallet)),
      avatarFor: (wallet) =>
        wallet ? avatarUrl(profiles[wallet.toLowerCase()]?.avatarPath) : undefined
    }
  }, [data])
}
